import { useState, useMemo, useEffect, useCallback, useRef, type CSSProperties } from 'react';
import { Button } from '@helios/ui/button';
import {
  NavIcon,
  SidebarToggleButton,
  useBrandTokens,
  orgName,
  orgLogoUrl,
} from '../../shell/shared';
// PRD.307 — client WhatsApp booking QR (optional, deployment-managed asset).
import { resolveWhatsappQrUrl } from '../../clients/whatsapp-qr-registry';
import { buildWhatsappQrPrintHtml, whatsappQrFilename } from './whatsapp-qr';
import { downloadFile } from '../../lib/download-file';
import { printHtmlViaIframe } from '../clinic/prescription-print/print-utils';
import { useAppointmentsContext } from '../../shell/AppointmentsShellLayout';
import { usePowerSyncQuery, usePowerSyncDb } from '../../adapters/powersync';
import { useFormatMoney } from '../../hooks/useFormatMoney';
import { useAuth } from '@helios/auth-firebase-web';
import {
  qAppConfig,
  qBookingSettings,
  qBookingFieldSettings,
  qBookingProviderSettings,
  qBookingServiceSettings,
  qAppointmentProviders,
  qAppointmentServicesWithCategory,
  mapBookingSettingsToUI,
  mapBookingFieldToUI,
  type BookingSettingsRow,
  type BookingFieldSettingRow,
  type BookingProviderSettingRow,
  type BookingServiceSettingRow,
  type AppointmentProviderRow,
  type AppointmentServiceWithCategoryRow,
  qAppointmentStatuses,
  type AppointmentStatusRow,
  type AppConfigRow,
} from '@helios/data-access';
import { ONLINE_BOOKING_DEFAULTS, DEFAULT_BOOKING_PATIENT_FIELDS } from '@helios/fixtures';
import {
  INBOUND_BOOKING_MENU_REPEAT_OPTIONS,
  DEFAULT_INBOUND_BOOKING_MENU_REPEAT_MINUTES,
} from '@helios/data-access';

// ─── Types ───

interface LocalProvider {
  providerId: string;
  name: string;
  specialty: string;
  email: string;
  avatarColor: string;
  active: boolean;
  enabled: boolean;
}

interface LocalService {
  serviceId: string;
  name: string;
  categoryName: string;
  categoryColorHex: string;
  duration: number;
  priceCents: number | null;
  active: boolean;
  enabled: boolean;
  durationOverride: number | null;
}

interface LocalBookingSettings {
  enabled: boolean;
  publicPath: string | null;
  defaultAppointmentStatusId: string | null;
  // PRD.306 — automatic WhatsApp booking menu.
  inboundBookingMenuEnabled: boolean;
  /** 0 = every message, null = never repeat. */
  inboundBookingMenuRepeatMinutes: number | null;
  // PRD.308 — extra reminder for appointments booked for today.
  sameDayBookingReminderEnabled: boolean;
}

interface LocalField {
  id: string;
  fieldKey: string;
  label: string;
  section: string;
  sectionColor: { bg: string; color: string };
  enabled: boolean;
  required: boolean;
  locked: boolean;
}

// ─── Field label + section color mapping ───

const FIELD_LABELS: Record<string, string> = {
  patient_name: 'Patient Name',
  mobile_country_code: 'Mobile Country Code',
  mobile_number: 'Mobile Number',
  email: 'Email',
  date_of_birth: 'Date of Birth',
  gender: 'Gender',
  address_line_1: 'Address Line 1',
  address_line_2: 'Address Line 2',
  city: 'City',
  state: 'State / Province',
  postal_code: 'Postal Code',
  country: 'Country',
  emergency_contact_name: 'Emergency Contact Name',
  emergency_contact_phone: 'Emergency Contact Phone',
};

const SECTION_COLORS: Record<string, { bg: string; color: string }> = {
  'Personal Info': { bg: '#fce7f3', color: '#9d174d' },
  Contact: { bg: '#fef3c7', color: '#92400e' },
  Address: { bg: '#e0e7ff', color: '#3730a3' },
  Emergency: { bg: '#fef9c3', color: '#854d0e' },
};

function fieldRowToLocal(row: BookingFieldSettingRow): LocalField {
  const ui = mapBookingFieldToUI(row);
  const section = ui.section || 'Other';
  return {
    id: ui.id,
    fieldKey: ui.fieldKey,
    label: FIELD_LABELS[ui.fieldKey] ?? ui.fieldKey,
    section,
    sectionColor: SECTION_COLORS[section] ?? { bg: '#f3f4f6', color: '#374151' },
    enabled: ui.enabled,
    required: ui.required,
    locked: ui.locked,
  };
}

// ─── Merge helpers ───

function mergeProviders(
  providerRows: AppointmentProviderRow[],
  settingRows: BookingProviderSettingRow[],
): LocalProvider[] {
  return providerRows.map((r) => {
    const setting = settingRows.find((s) => s.provider_id === r.id);
    return {
      providerId: r.id,
      name: r.full_name ?? '',
      specialty: r.specialty ?? '',
      email: r.email ?? '',
      avatarColor: r.avatar_color ?? '#2980b9',
      active: r.active === 1,
      enabled: setting ? setting.enabled == 1 : true, // default enabled
    };
  });
}

function mergeServices(
  serviceRows: AppointmentServiceWithCategoryRow[],
  settingRows: BookingServiceSettingRow[],
): LocalService[] {
  return serviceRows.map((r) => {
    const setting = settingRows.find((s) => s.service_id === r.id);
    return {
      serviceId: r.id,
      name: r.name ?? '',
      categoryName: r.category_name ?? '',
      categoryColorHex: r.category_color_hex ?? '#3b82f6',
      duration: r.typical_duration_mins ?? 0,
      priceCents: r.price_cents,
      active: r.active === 1,
      enabled: setting ? setting.enabled == 1 : false, // default disabled
      durationOverride: setting ? (setting.duration_override_mins ?? null) : null,
    };
  });
}

// ─── Toggle ───

function Toggle({
  checked,
  onChange,
  disabled,
}: {
  checked: boolean;
  onChange: (val: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => !disabled && onChange(!checked)}
      style={{
        width: 44,
        height: 24,
        borderRadius: 'var(--radius-full)',
        background: checked ? 'var(--color-primary)' : 'var(--border)',
        border: 'none',
        padding: 2,
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'background 150ms ease',
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0,
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <span
        style={{
          width: 20,
          height: 20,
          borderRadius: 'var(--radius-full)',
          background: '#ffffff',
          transform: checked ? 'translateX(20px)' : 'translateX(0)',
          transition: 'transform 150ms ease',
          boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
        }}
      />
    </button>
  );
}

// ─── Category Badge ───

function Badge({ label, bg, color }: { label: string; bg: string; color: string }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 24,
        padding: '0 var(--sp-2)',
        borderRadius: 'var(--radius-full)',
        background: bg,
        color,
        fontSize: 'var(--font-size-xs)',
        fontWeight: 'var(--font-weight-medium)',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
}

// ─── Required Badge ───

function RequiredBadge() {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        height: 24,
        padding: '0 var(--sp-2)',
        borderRadius: 'var(--radius-full)',
        background: '#fef3c7',
        color: '#92400e',
        fontSize: 'var(--font-size-xs)',
        fontWeight: 'var(--font-weight-medium)',
        whiteSpace: 'nowrap',
      }}
    >
      <NavIcon name="shield-alert" />
      Required
    </span>
  );
}

// ─── "From Service" badge ───

function FromServiceBadge() {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 24,
        padding: '0 var(--sp-2)',
        borderRadius: 'var(--radius-full)',
        background: '#dbeafe',
        color: '#1e40af',
        fontSize: 'var(--font-size-xs)',
        fontWeight: 'var(--font-weight-medium)',
        whiteSpace: 'nowrap',
      }}
    >
      From Service
    </span>
  );
}

/**
 * Public Booking Link card visibility.
 *
 * The public booking page is not being offered to clinics yet, so the card is
 * hidden rather than deleted — the link, Copy Link and Share actions all still
 * work and are one edit away from returning. Nothing else about public booking
 * changes: the Availability toggle and the underlying `booking_online_settings`
 * row are untouched.
 */
const SHOW_PUBLIC_BOOKING_LINK = false;

// ─── Card ───

function Card({ children, style }: { children: React.ReactNode; style?: CSSProperties }) {
  return (
    <div
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--sp-6)',
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// ─── Hex to badge bg helper ───

function hexToBadgeBg(hex: string): string {
  // Lighten the hex color for badge background
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, 0.15)`;
}

// ─── Online Booking Settings Page ───

export function OnlineBookingSettingsPage() {
  useBrandTokens();
  const { db } = usePowerSyncDb();
  const { claims } = useAuth();
  const orgId = claims?.activeOrg ?? '';
  const fmt = useFormatMoney();
  const { collapsed, onToggleSidebar } = useAppointmentsContext();

  // ─── DB queries ───
  const { data: settingsRows = [] } = usePowerSyncQuery<BookingSettingsRow>(qBookingSettings());
  // PRD.307 — clinic identity for the printable QR sheet. app_config is the
  // admin-editable source of truth; the build-time env name is the fallback for
  // a clinic that has not saved Clinic Settings yet.
  const { data: appConfigRows = [] } = usePowerSyncQuery<AppConfigRow>(qAppConfig());
  const clientId = import.meta.env.VITE_CLIENT_ID || '';
  // Availability is decided solely by the presence of a client QR asset —
  // never inferred from the clinic phone number (FR3).
  const whatsappQrUrl = useMemo(() => resolveWhatsappQrUrl(clientId), [clientId]);
  const qrClinicName = appConfigRows[0]?.org_name?.trim() || orgName;

  const handleDownloadQr = useCallback(() => {
    if (!whatsappQrUrl) return;
    // The bundled PNG itself, not a screenshot of the card (FR5).
    void downloadFile(whatsappQrUrl, whatsappQrFilename(clientId));
  }, [whatsappQrUrl, clientId]);

  const handlePrintQr = useCallback(() => {
    if (!whatsappQrUrl) return;
    // Isolated iframe → the admin UI is never part of the printout, and the
    // helper waits for the QR to decode before printing (FR6/FR7).
    printHtmlViaIframe(
      buildWhatsappQrPrintHtml({
        clinicName: qrClinicName,
        qrUrl: whatsappQrUrl,
        logoUrl: orgLogoUrl || null,
      }),
      `${qrClinicName} — WhatsApp booking QR`,
    );
  }, [whatsappQrUrl, qrClinicName]);
  const { data: fieldRows = [] } =
    usePowerSyncQuery<BookingFieldSettingRow>(qBookingFieldSettings());
  const { data: statusRows = [] } = usePowerSyncQuery<AppointmentStatusRow>(qAppointmentStatuses());
  const { data: providerRows = [] } =
    usePowerSyncQuery<AppointmentProviderRow>(qAppointmentProviders());
  const { data: providerSettingRows = [] } = usePowerSyncQuery<BookingProviderSettingRow>(
    qBookingProviderSettings(),
  );
  const { data: serviceRows = [] } = usePowerSyncQuery<AppointmentServiceWithCategoryRow>(
    qAppointmentServicesWithCategory(),
  );
  const { data: serviceSettingRows = [] } =
    usePowerSyncQuery<BookingServiceSettingRow>(qBookingServiceSettings());

  // ─── Local buffered state ───
  const [localSettings, setLocalSettings] = useState<LocalBookingSettings>({
    enabled: ONLINE_BOOKING_DEFAULTS.enabled,
    publicPath: ONLINE_BOOKING_DEFAULTS.public_path,
    defaultAppointmentStatusId: ONLINE_BOOKING_DEFAULTS.default_appointment_status_id,
    inboundBookingMenuEnabled: true,
    inboundBookingMenuRepeatMinutes: DEFAULT_INBOUND_BOOKING_MENU_REPEAT_MINUTES,
    sameDayBookingReminderEnabled: false,
  });
  const [localFields, setLocalFields] = useState<LocalField[]>([]);
  const [localProviders, setLocalProviders] = useState<LocalProvider[]>([]);
  const [localServices, setLocalServices] = useState<LocalService[]>([]);
  const [providerSearch, setProviderSearch] = useState('');
  const [serviceSearch, setServiceSearch] = useState('');
  const [fieldSearch, setFieldSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');

  const initializedRef = useRef(false);
  const [savedSnapshot, setSavedSnapshot] = useState('');

  // ─── Populate from DB on first load ───
  // Wait for base data + settings queries to have settled (settings may be empty if never saved)
  const allDataReady = fieldRows.length > 0 && providerRows.length > 0 && serviceRows.length > 0;

  // Delay initialization by one tick after allDataReady so settings rows have time to arrive
  const [initReady, setInitReady] = useState(false);
  useEffect(() => {
    if (!allDataReady || initReady) return;
    const t = setTimeout(() => setInitReady(true), 100);
    return () => clearTimeout(t);
  }, [allDataReady, initReady]);

  useEffect(() => {
    if (initializedRef.current) return;
    if (!initReady) return;

    // Settings row
    if (settingsRows.length > 0) {
      const ui = mapBookingSettingsToUI(settingsRows[0]);
      setLocalSettings({
        enabled: ui.enabled,
        publicPath: ui.publicPath,
        defaultAppointmentStatusId: ui.defaultAppointmentStatusId,
        inboundBookingMenuEnabled: ui.inboundBookingMenuEnabled,
        inboundBookingMenuRepeatMinutes: ui.inboundBookingMenuRepeatMinutes,
        sameDayBookingReminderEnabled: ui.sameDayBookingReminderEnabled,
      });
    }

    // Field rows
    setLocalFields(fieldRows.map(fieldRowToLocal));

    // Providers: merge base rows with settings
    const mergedP = mergeProviders(providerRows, providerSettingRows);
    setLocalProviders(mergedP);

    // Services: merge base rows with settings
    const mergedS = mergeServices(serviceRows, serviceSettingRows);
    setLocalServices(mergedS);

    // Compute snapshot inline so it matches the local state we just set
    const initSettings =
      settingsRows.length > 0
        ? (() => {
            const ui = mapBookingSettingsToUI(settingsRows[0]);
            return {
              enabled: ui.enabled,
              publicPath: ui.publicPath,
              defaultAppointmentStatusId: ui.defaultAppointmentStatusId,
            };
          })()
        : {
            enabled: ONLINE_BOOKING_DEFAULTS.enabled,
            publicPath: ONLINE_BOOKING_DEFAULTS.public_path,
            defaultAppointmentStatusId: ONLINE_BOOKING_DEFAULTS.default_appointment_status_id,
          };
    const initFields = fieldRows.map(fieldRowToLocal);
    setSavedSnapshot(
      JSON.stringify({
        s: initSettings,
        f: initFields,
        p: mergedP.map((p) => ({ providerId: p.providerId, enabled: p.enabled })),
        sv: mergedS.map((s) => ({
          serviceId: s.serviceId,
          enabled: s.enabled,
          durationOverride: s.durationOverride,
        })),
      }),
    );

    initializedRef.current = true;
  }, [
    initReady,
    settingsRows,
    fieldRows,
    providerRows,
    providerSettingRows,
    serviceRows,
    serviceSettingRows,
  ]);

  const isDirty = useMemo(() => {
    if (!savedSnapshot) return false;
    const current = JSON.stringify({
      s: localSettings,
      f: localFields,
      p: localProviders.map((p) => ({ providerId: p.providerId, enabled: p.enabled })),
      sv: localServices.map((s) => ({
        serviceId: s.serviceId,
        enabled: s.enabled,
        durationOverride: s.durationOverride,
      })),
    });
    return current !== savedSnapshot;
  }, [localSettings, localFields, localProviders, localServices, savedSnapshot]);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2500);
  }, []);

  // ─── Providers bulk toggle + filter ───
  const activeProviders = localProviders.filter((p) => p.active);
  const allProvidersEnabled = activeProviders.length > 0 && activeProviders.every((p) => p.enabled);

  const handleToggleAllProviders = () => {
    const newVal = !allProvidersEnabled;
    setLocalProviders((prev) => prev.map((p) => (p.active ? { ...p, enabled: newVal } : p)));
  };

  const handleToggleProvider = (providerId: string) => {
    setLocalProviders((prev) =>
      prev.map((p) => (p.providerId === providerId ? { ...p, enabled: !p.enabled } : p)),
    );
  };

  const filteredProviders = useMemo(() => {
    if (!providerSearch.trim()) return localProviders;
    const q = providerSearch.toLowerCase();
    return localProviders.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        p.specialty.toLowerCase().includes(q) ||
        p.email.toLowerCase().includes(q),
    );
  }, [localProviders, providerSearch]);

  // ─── Services bulk toggle + filter ───
  const activeServices = localServices.filter((s) => s.active);
  const allServicesEnabled = activeServices.length > 0 && activeServices.every((s) => s.enabled);

  const handleToggleAllServices = () => {
    const newVal = !allServicesEnabled;
    setLocalServices((prev) => prev.map((s) => (s.active ? { ...s, enabled: newVal } : s)));
  };

  const handleToggleService = (serviceId: string) => {
    setLocalServices((prev) =>
      prev.map((s) => (s.serviceId === serviceId ? { ...s, enabled: !s.enabled } : s)),
    );
  };

  const handleServiceDurationOverride = (serviceId: string, value: string) => {
    const num = value === '' ? null : parseInt(value, 10);
    setLocalServices((prev) =>
      prev.map((s) =>
        s.serviceId === serviceId
          ? { ...s, durationOverride: num !== null && !isNaN(num) ? num : null }
          : s,
      ),
    );
  };

  const filteredServices = useMemo(() => {
    if (!serviceSearch.trim()) return localServices;
    const q = serviceSearch.toLowerCase();
    return localServices.filter(
      (s) => s.name.toLowerCase().includes(q) || s.categoryName.toLowerCase().includes(q),
    );
  }, [localServices, serviceSearch]);

  // ─── Fields filter ───
  const filteredFields = useMemo(() => {
    if (!fieldSearch.trim()) return localFields;
    const q = fieldSearch.toLowerCase();
    return localFields.filter(
      (f) => f.label.toLowerCase().includes(q) || f.fieldKey.toLowerCase().includes(q),
    );
  }, [localFields, fieldSearch]);

  const handleToggleField = (id: string) => {
    setLocalFields((prev) =>
      prev.map((f) => (f.id === id && !f.locked ? { ...f, enabled: !f.enabled } : f)),
    );
  };

  // ─── Save ───
  const handleSave = useCallback(async () => {
    if (!db || saving) return;
    setSaving(true);
    try {
      const now = new Date().toISOString();

      // 1. Update booking_online_settings
      if (settingsRows.length > 0) {
        const id = settingsRows[0].id;
        await db.execute(
          `UPDATE booking_online_settings SET
            enabled = ?,
            public_path = ?,
            default_appointment_status_id = ?,
            inbound_booking_menu_enabled = ?,
            inbound_booking_menu_repeat_minutes = ?,
            same_day_booking_reminder_enabled = ?,
            updated_at = ?
          WHERE id = ?`,
          [
            localSettings.enabled ? 1 : 0,
            localSettings.publicPath,
            localSettings.defaultAppointmentStatusId,
            localSettings.inboundBookingMenuEnabled ? 1 : 0,
            // null is meaningful ("never repeat") — pass it through unchanged.
            localSettings.inboundBookingMenuRepeatMinutes,
            localSettings.sameDayBookingReminderEnabled ? 1 : 0,
            now,
            id,
          ],
        );
      }

      // 2. Update field settings
      for (const field of localFields) {
        await db.execute(
          `UPDATE booking_online_patient_field_settings SET
            enabled = ?,
            required = ?,
            locked = ?,
            updated_at = ?
          WHERE id = ?`,
          [field.enabled ? 1 : 0, field.required ? 1 : 0, field.locked ? 1 : 0, now, field.id],
        );
      }

      // 3. Upsert provider settings
      for (const p of localProviders) {
        await db.execute(
          `INSERT INTO booking_online_provider_settings
            (id, org_id, clinic_id, provider_id, enabled, created_at, updated_at)
          VALUES (?, ?, NULL, ?, ?, ?, ?)`,
          [crypto.randomUUID(), orgId, p.providerId, p.enabled ? 1 : 0, now, now],
        );
      }

      // 4. Upsert service settings
      for (const s of localServices) {
        await db.execute(
          `INSERT INTO booking_online_service_settings
            (id, org_id, clinic_id, service_id, enabled, duration_override_mins, created_at, updated_at)
          VALUES (?, ?, NULL, ?, ?, ?, ?, ?)`,
          [
            crypto.randomUUID(),
            orgId,
            s.serviceId,
            s.enabled ? 1 : 0,
            s.durationOverride,
            now,
            now,
          ],
        );
      }

      const snap = JSON.stringify({
        s: localSettings,
        f: localFields,
        p: localProviders.map((p) => ({ providerId: p.providerId, enabled: p.enabled })),
        sv: localServices.map((s) => ({
          serviceId: s.serviceId,
          enabled: s.enabled,
          durationOverride: s.durationOverride,
        })),
      });
      setSavedSnapshot(snap);
      showToast('Settings saved');
    } catch (err) {
      console.error('[OnlineBookingSettings] save error:', err);
      showToast('Failed to save settings');
    } finally {
      setSaving(false);
    }
  }, [
    db,
    saving,
    localSettings,
    localFields,
    localProviders,
    localServices,
    settingsRows,
    orgId,
    showToast,
  ]);

  // ─── Reset to Default ───
  const handleReset = useCallback(async () => {
    if (!db) return;

    const resetSettings: LocalBookingSettings = {
      enabled: ONLINE_BOOKING_DEFAULTS.enabled,
      publicPath: ONLINE_BOOKING_DEFAULTS.public_path,
      defaultAppointmentStatusId: ONLINE_BOOKING_DEFAULTS.default_appointment_status_id,
      // PRD.306 defaults: automatic menu on, repeat after 24h.
      inboundBookingMenuEnabled: true,
      inboundBookingMenuRepeatMinutes: DEFAULT_INBOUND_BOOKING_MENU_REPEAT_MINUTES,
      // PRD.308 default: OFF.
      sameDayBookingReminderEnabled: false,
    };
    setLocalSettings(resetSettings);

    // Reset field settings to fixture defaults
    const resetFields = localFields.map((f) => {
      const fixture = DEFAULT_BOOKING_PATIENT_FIELDS.find((d) => d.field_key === f.fieldKey);
      if (fixture) {
        return {
          ...f,
          enabled: fixture.enabled,
          required: fixture.required,
          locked: fixture.locked,
        };
      }
      return f;
    });
    setLocalFields(resetFields);

    // Reset providers: all active providers enabled
    const resetProviders = localProviders.map((p) => ({ ...p, enabled: p.active ? true : false }));
    setLocalProviders(resetProviders);

    // Reset services: all disabled (default)
    const resetServices = localServices.map((s) => ({
      ...s,
      enabled: false,
      durationOverride: null,
    }));
    setLocalServices(resetServices);

    // Persist reset
    try {
      const now = new Date().toISOString();

      if (settingsRows.length > 0) {
        const id = settingsRows[0].id;
        await db.execute(
          `UPDATE booking_online_settings SET
            enabled = ?,
            public_path = ?,
            default_appointment_status_id = ?,
            updated_at = ?
          WHERE id = ?`,
          [
            resetSettings.enabled ? 1 : 0,
            resetSettings.publicPath,
            resetSettings.defaultAppointmentStatusId,
            now,
            id,
          ],
        );
      }

      for (const field of resetFields) {
        await db.execute(
          `UPDATE booking_online_patient_field_settings SET
            enabled = ?,
            required = ?,
            locked = ?,
            updated_at = ?
          WHERE id = ?`,
          [field.enabled ? 1 : 0, field.required ? 1 : 0, field.locked ? 1 : 0, now, field.id],
        );
      }

      // Upsert provider settings
      for (const p of resetProviders) {
        await db.execute(
          `INSERT INTO booking_online_provider_settings
            (id, org_id, clinic_id, provider_id, enabled, created_at, updated_at)
          VALUES (?, ?, NULL, ?, ?, ?, ?)`,
          [crypto.randomUUID(), orgId, p.providerId, p.enabled ? 1 : 0, now, now],
        );
      }

      // Upsert service settings
      for (const s of resetServices) {
        await db.execute(
          `INSERT INTO booking_online_service_settings
            (id, org_id, clinic_id, service_id, enabled, duration_override_mins, created_at, updated_at)
          VALUES (?, ?, NULL, ?, ?, ?, ?, ?)`,
          [
            crypto.randomUUID(),
            orgId,
            s.serviceId,
            s.enabled ? 1 : 0,
            s.durationOverride,
            now,
            now,
          ],
        );
      }

      const snap = JSON.stringify({
        s: resetSettings,
        f: resetFields,
        p: resetProviders.map((p) => ({ providerId: p.providerId, enabled: p.enabled })),
        sv: resetServices.map((s) => ({
          serviceId: s.serviceId,
          enabled: s.enabled,
          durationOverride: s.durationOverride,
        })),
      });
      setSavedSnapshot(snap);
      showToast('Reset to defaults');
    } catch (err) {
      console.error('[OnlineBookingSettings] reset error:', err);
      showToast('Failed to reset');
    }
  }, [db, localFields, localProviders, localServices, settingsRows, orgId, showToast]);

  // ─── Status dropdown options from DB ───
  const statusOptions = useMemo(() => {
    return statusRows
      .filter((s) => s.active === 1)
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
      .map((s) => ({ value: s.id, label: s.label ?? '' }));
  }, [statusRows]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Toast */}
      {toast && (
        <div
          style={{
            position: 'fixed',
            top: 'var(--sp-4)',
            right: 'var(--sp-4)',
            zIndex: 9999,
            padding: 'var(--sp-3) var(--sp-5)',
            borderRadius: 'var(--radius-md)',
            background: toast.includes('Failed') ? '#fecaca' : '#dcfce7',
            color: toast.includes('Failed') ? '#991b1b' : '#166534',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-medium)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
          }}
        >
          {toast}
        </div>
      )}

      {/* Page Header */}
      <div
        style={{
          height: 'var(--clinic-header-height)',
          padding: '0 var(--sp-6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid var(--border)',
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
          {onToggleSidebar && (
            <SidebarToggleButton collapsed={collapsed} onToggle={onToggleSidebar} />
          )}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
              <h1
                style={{
                  fontSize: 'var(--font-size-xl2)',
                  fontWeight: 'var(--font-weight-semi)',
                  lineHeight: 'var(--line-height-xl2)',
                  letterSpacing: '-0.44px',
                  color: 'var(--text)',
                  margin: 0,
                }}
              >
                Online Booking
              </h1>
              <span
                style={{
                  height: 22,
                  padding: '0 var(--sp-2)',
                  borderRadius: 'var(--radius-xs)',
                  background: 'var(--color-warning-bg)',
                  color: 'var(--color-warning-text)',
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: 'var(--font-weight-medium)',
                  lineHeight: '22px',
                  letterSpacing: '0.12px',
                }}
              >
                Admin only
              </span>
            </div>
            <p
              style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--text-muted)',
                margin: '2px 0 0',
              }}
            >
              Configure the public booking link and online booking preferences
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
          <button
            type="button"
            onClick={handleReset}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--sp-2)',
              background: 'none',
              border: 'none',
              color: 'var(--text-muted)',
              fontSize: 'var(--font-size-sm)',
              cursor: 'pointer',
              padding: 0,
            }}
          >
            <NavIcon name="refresh" />
            Reset to Default
          </button>
          <Button variant="gradient" size="md" disabled={!isDirty || saving} onClick={handleSave}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {/* Scrollable Content */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: 'var(--sp-6)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-5)',
        }}
      >
        {/* Card 1 — Online Booking Availability */}
        <Card>
          <div
            style={{
              fontSize: 'var(--font-size-lg)',
              fontWeight: 'var(--font-weight-semi)',
              color: 'var(--text)',
              marginBottom: 'var(--sp-4)',
            }}
          >
            Online Booking Availability
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 'var(--font-size-base)',
                  fontWeight: 'var(--font-weight-medium)',
                  color: 'var(--text)',
                }}
              >
                Enable public booking
              </div>
              <div
                style={{
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--text-muted)',
                  marginTop: 2,
                }}
              >
                Allow patients to book appointments through the public link
              </div>
            </div>
            <Toggle
              checked={localSettings.enabled}
              onChange={(val) => setLocalSettings((prev) => ({ ...prev, enabled: val }))}
            />
          </div>
        </Card>

        {/* PRD.307 — WhatsApp booking QR. Hidden entirely when this client has
            no whatsapp-qr.png asset, so nothing broken is ever rendered. */}
        {whatsappQrUrl && (
          <Card>
            <div
              style={{
                fontSize: 'var(--font-size-lg)',
                fontWeight: 'var(--font-weight-semi)',
                color: 'var(--text)',
                marginBottom: 'var(--sp-4)',
              }}
            >
              WhatsApp
            </div>
            <div style={{ fontWeight: 'var(--font-weight-medium)', color: 'var(--text)' }}>
              WhatsApp Booking QR
            </div>
            <div
              style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--text-muted)',
                marginTop: 2,
              }}
            >
              Patients can scan this QR code to start a WhatsApp conversation with the clinic.
            </div>

            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 'var(--sp-5)',
                marginTop: 'var(--sp-4)',
                flexWrap: 'wrap',
              }}
            >
              {/* Square box + object-fit:contain shows the original PNG whole —
                  no cropping, no overlay, quiet zone intact (FR4). */}
              <img
                src={whatsappQrUrl}
                alt={`WhatsApp booking QR code for ${qrClinicName}`}
                width={200}
                height={200}
                style={{
                  width: 200,
                  height: 200,
                  objectFit: 'contain',
                  background: '#ffffff',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-md)',
                  padding: 'var(--sp-2)',
                }}
              />
              <div style={{ display: 'flex', gap: 'var(--sp-3)', flexWrap: 'wrap' }}>
                {/* Immediate actions — deliberately independent of Save Changes (FR10). */}
                <Button variant="outline" size="md" onClick={handleDownloadQr}>
                  Download QR
                </Button>
                <Button variant="outline" size="md" onClick={handlePrintQr}>
                  Print QR
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* PRD.306 — Automatic Booking Menu (WhatsApp) */}
        <Card>
          <div
            style={{
              fontSize: 'var(--font-size-lg)',
              fontWeight: 'var(--font-weight-semi)',
              color: 'var(--text)',
              marginBottom: 'var(--sp-4)',
            }}
          >
            Automatic Booking Menu
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 'var(--sp-4)',
            }}
          >
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 'var(--font-weight-medium)', color: 'var(--text)' }}>
                Send booking menu when a patient messages us
              </div>
              <div
                style={{
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--text-muted)',
                  marginTop: 2,
                }}
              >
                Replies to an inbound WhatsApp message with your approved booking template — the one
                carrying the Book Now button that opens the appointment Flow.
              </div>
            </div>
            <Toggle
              checked={localSettings.inboundBookingMenuEnabled}
              onChange={(val) =>
                setLocalSettings((prev) => ({ ...prev, inboundBookingMenuEnabled: val }))
              }
            />
          </div>

          <div style={{ marginTop: 'var(--sp-4)', maxWidth: 320 }}>
            <label
              htmlFor="inbound-menu-repeat"
              style={{
                display: 'block',
                fontSize: 'var(--font-size-sm)',
                color: 'var(--text-muted)',
                marginBottom: 'var(--sp-2)',
              }}
            >
              Repeat menu
            </label>
            <select
              id="inbound-menu-repeat"
              disabled={!localSettings.inboundBookingMenuEnabled}
              value={
                localSettings.inboundBookingMenuRepeatMinutes === null
                  ? 'never'
                  : String(localSettings.inboundBookingMenuRepeatMinutes)
              }
              onChange={(e) =>
                setLocalSettings((prev) => ({
                  ...prev,
                  inboundBookingMenuRepeatMinutes:
                    e.target.value === 'never' ? null : Number(e.target.value),
                }))
              }
              style={{
                width: '100%',
                height: 'var(--input-height)',
                padding: '0 var(--input-padding-x)',
                borderRadius: 'var(--input-radius)',
                border: '1px solid var(--border-input)',
                background: 'var(--input-bg)',
                color: 'var(--input-text)',
                fontSize: 'var(--font-size-base)',
                fontFamily: 'inherit',
                opacity: localSettings.inboundBookingMenuEnabled ? 1 : 0.6,
              }}
            >
              {INBOUND_BOOKING_MENU_REPEAT_OPTIONS.map((opt) => (
                <option key={opt.label} value={opt.value === null ? 'never' : String(opt.value)}>
                  {opt.label}
                </option>
              ))}
            </select>
            <div
              style={{
                marginTop: 'var(--sp-2)',
                fontSize: 'var(--font-size-xs)',
                color: 'var(--text-muted)',
              }}
            >
              How long to wait before sending the menu again to the same patient. The first message
              always gets one; “Never repeat” sends it only once.
            </div>
          </div>

          {/* PRD.308 — same-day booking reminders. Default OFF: a patient booked
              for later today already receives the booking confirmation, and the
              extra reminder arriving seconds later reads as a duplicate. */}
          <div
            style={{
              marginTop: 'var(--sp-6)',
              paddingTop: 'var(--sp-5)',
              borderTop: '1px solid var(--border)',
            }}
          >
            <div
              style={{
                fontSize: 'var(--font-size-base)',
                fontWeight: 'var(--font-weight-semi)',
                color: 'var(--text)',
                marginBottom: 'var(--sp-3)',
              }}
            >
              Same-day booking reminders
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 'var(--sp-4)',
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 'var(--font-weight-medium)', color: 'var(--text)' }}>
                  Send an additional reminder for appointments booked today
                </div>
                <div
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--text-muted)',
                    marginTop: 2,
                  }}
                >
                  When disabled, newly booked same-day appointments receive only the booking
                  confirmation. Scheduled and manual reminders are unaffected.
                </div>
              </div>
              <Toggle
                checked={localSettings.sameDayBookingReminderEnabled}
                onChange={(val) =>
                  setLocalSettings((prev) => ({ ...prev, sameDayBookingReminderEnabled: val }))
                }
              />
            </div>
          </div>
        </Card>

        {/* Card 2 — Public Booking Link.
            Hidden for now (public booking is not being offered yet); the card
            and its Copy Link / Share handlers are intentionally kept intact —
            flip SHOW_PUBLIC_BOOKING_LINK back to true to restore it. */}
        {SHOW_PUBLIC_BOOKING_LINK && (
          <>
            <Card>
              <div
                style={{
                  fontSize: 'var(--font-size-lg)',
                  fontWeight: 'var(--font-weight-semi)',
                  color: 'var(--text)',
                  marginBottom: 'var(--sp-4)',
                }}
              >
                Public Booking Link
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
                <div style={{ flex: 1 }}>
                  <input
                    type="text"
                    readOnly
                    value={`https://${orgId || 'your-clinic'}.powercare.health/book`}
                    style={{
                      ...inputReadOnlyStyle,
                      width: '100%',
                    }}
                  />
                  <span
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--text-muted)',
                      marginTop: 4,
                      display: 'block',
                    }}
                  >
                    Read only
                  </span>
                </div>
                <button type="button" style={outlineBtnStyle}>
                  <NavIcon name="clipboard" />
                  Copy Link
                </button>
                <button type="button" style={outlineBtnStyle}>
                  <NavIcon name="mail" />
                  Share
                </button>
              </div>
              <div
                style={{
                  marginTop: 'var(--sp-4)',
                  padding: 'var(--sp-3) var(--sp-4)',
                  borderRadius: 'var(--radius-md)',
                  background: '#fef3c7',
                  fontSize: 'var(--font-size-sm)',
                  color: '#92400e',
                }}
              >
                <strong>Note:</strong> Changes may take a few minutes to reflect on the public
                booking page.
              </div>
            </Card>
          </>
        )}

        {/* Card 3 — Providers */}
        <Card>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 'var(--font-size-lg)',
                  fontWeight: 'var(--font-weight-semi)',
                  color: 'var(--text)',
                }}
              >
                Providers
              </div>
              <div
                style={{
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--text-muted)',
                  marginTop: 2,
                }}
              >
                Select which providers can receive online bookings
              </div>
            </div>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <div
                style={{
                  position: 'absolute',
                  left: 12,
                  color: 'var(--text-muted)',
                  display: 'flex',
                  pointerEvents: 'none',
                }}
              >
                <NavIcon name="search" />
              </div>
              <input
                type="text"
                placeholder="Search providers..."
                value={providerSearch}
                onChange={(e) => setProviderSearch(e.target.value)}
                style={searchInputStyle}
              />
            </div>
          </div>

          {/* Bulk toggle */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--sp-3)',
              marginTop: 'var(--sp-4)',
              marginBottom: 'var(--sp-4)',
            }}
          >
            <Toggle checked={allProvidersEnabled} onChange={handleToggleAllProviders} />
            <span
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-medium)',
                color: 'var(--text)',
              }}
            >
              Enable all providers
            </span>
          </div>

          {/* Provider list */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
            {filteredProviders.map((provider) => (
              <div
                key={provider.providerId}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--sp-3)',
                  opacity: provider.enabled ? 1 : 0.6,
                }}
              >
                <Toggle
                  checked={provider.enabled}
                  onChange={() => handleToggleProvider(provider.providerId)}
                />
                {/* Avatar */}
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 'var(--radius-full)',
                    background: `linear-gradient(135deg, ${provider.avatarColor}, ${provider.avatarColor}dd)`,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontSize: 'var(--font-size-xs)',
                    fontWeight: 'var(--font-weight-semi)',
                  }}
                >
                  {provider.name
                    .replace(/^Dr\.\s*/i, '')
                    .split(/\s+/)
                    .map((w) => w[0])
                    .join('')
                    .toUpperCase()
                    .slice(0, 2) || '?'}
                </div>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: 'var(--font-weight-medium)',
                      color: 'var(--text)',
                    }}
                  >
                    {provider.name}
                  </div>
                  <div
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--text-muted)',
                    }}
                  >
                    {provider.specialty || provider.email || '—'}
                  </div>
                </div>
                {!provider.active && <Badge label="Inactive" bg="#f3f4f6" color="#6b7280" />}
              </div>
            ))}
            {filteredProviders.length === 0 && (
              <div
                style={{
                  padding: 'var(--sp-4)',
                  textAlign: 'center',
                  color: 'var(--text-muted)',
                  fontSize: 'var(--font-size-sm)',
                }}
              >
                {providerSearch
                  ? 'No providers match your search.'
                  : 'No providers configured yet.'}
              </div>
            )}
          </div>
        </Card>

        {/* Card 4 — Bookable Services */}
        <Card style={{ padding: 0 }}>
          <div style={{ padding: 'var(--sp-6) var(--sp-6) 0' }}>
            <div
              style={{
                fontSize: 'var(--font-size-lg)',
                fontWeight: 'var(--font-weight-semi)',
                color: 'var(--text)',
              }}
            >
              Bookable Services (Online Booking)
            </div>
            <div
              style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--text-muted)',
                marginTop: 2,
                marginBottom: 'var(--sp-4)',
              }}
            >
              Configure which services patients can book online
            </div>

            {/* Bulk toggle + Search */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 'var(--sp-4)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
                <Toggle checked={allServicesEnabled} onChange={handleToggleAllServices} />
                <span
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 'var(--font-weight-medium)',
                    color: 'var(--text)',
                  }}
                >
                  Enable all services
                </span>
              </div>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <div
                  style={{
                    position: 'absolute',
                    left: 12,
                    color: 'var(--text-muted)',
                    display: 'flex',
                    pointerEvents: 'none',
                  }}
                >
                  <NavIcon name="search" />
                </div>
                <input
                  type="text"
                  placeholder="Search services..."
                  value={serviceSearch}
                  onChange={(e) => setServiceSearch(e.target.value)}
                  style={searchInputStyle}
                />
              </div>
            </div>
          </div>

          {/* Services table */}
          <div>
            {/* Header */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '70px 1fr 130px 200px',
                gap: 'var(--sp-3)',
                alignItems: 'center',
                padding: 'var(--sp-3) var(--sp-6)',
                borderBottom: '1px solid var(--border)',
                borderTop: '1px solid var(--border)',
              }}
            >
              <span style={thStyle}>ENABLED</span>
              <span style={thStyle}>SERVICE</span>
              <span style={thStyle}>CATEGORY</span>
              <span style={thStyle}>DEFAULT DURATION</span>
            </div>

            {/* Rows */}
            {filteredServices.map((svc) => (
              <div
                key={svc.serviceId}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '70px 1fr 130px 200px',
                  gap: 'var(--sp-3)',
                  alignItems: 'center',
                  padding: 'var(--sp-3) var(--sp-6)',
                  borderBottom: '1px solid var(--border)',
                  opacity: svc.enabled ? 1 : 0.6,
                }}
              >
                <Toggle checked={svc.enabled} onChange={() => handleToggleService(svc.serviceId)} />
                <span
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 'var(--font-weight-medium)',
                    color: 'var(--text)',
                  }}
                >
                  {svc.name}
                </span>
                <Badge
                  label={svc.categoryName || '—'}
                  bg={hexToBadgeBg(svc.categoryColorHex)}
                  color={svc.categoryColorHex}
                />
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
                  {svc.durationOverride == null && <FromServiceBadge />}
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder={`${svc.duration}`}
                    value={svc.durationOverride ?? ''}
                    onChange={(e) =>
                      handleServiceDurationOverride(
                        svc.serviceId,
                        e.target.value.replace(/\D/g, ''),
                      )
                    }
                    style={{
                      width: 56,
                      height: 28,
                      padding: '0 var(--sp-2)',
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--radius-sm)',
                      fontSize: 'var(--font-size-sm)',
                      color: svc.durationOverride != null ? 'var(--text)' : 'var(--text-muted)',
                      background: 'var(--bg)',
                      textAlign: 'center',
                    }}
                  />
                  <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                    min
                  </span>
                  {svc.durationOverride != null && (
                    <button
                      type="button"
                      title="Reset to service default"
                      onClick={() => handleServiceDurationOverride(svc.serviceId, '')}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 24,
                        height: 24,
                        borderRadius: 'var(--radius-full)',
                        border: '1px solid var(--border)',
                        background: 'var(--bg-surface)',
                        cursor: 'pointer',
                        padding: 0,
                        color: 'var(--text-muted)',
                        fontSize: 12,
                      }}
                    >
                      <NavIcon name="x" />
                    </button>
                  )}
                  <span
                    style={{
                      fontSize: 'var(--font-size-sm)',
                      color: 'var(--text-muted)',
                    }}
                  >
                    {svc.priceCents == null ? '—' : fmt(svc.priceCents / 100)}
                  </span>
                </div>
              </div>
            ))}

            {filteredServices.length === 0 && (
              <div
                style={{
                  padding: 'var(--sp-6)',
                  textAlign: 'center',
                  color: 'var(--text-muted)',
                  fontSize: 'var(--font-size-sm)',
                }}
              >
                {serviceSearch ? 'No services match your search.' : 'No services configured yet.'}
              </div>
            )}
          </div>
        </Card>

        {/* Card 5 — Patient Fields */}
        <Card style={{ padding: 0 }}>
          <div style={{ padding: 'var(--sp-6) var(--sp-6) 0' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: 'var(--font-size-lg)',
                    fontWeight: 'var(--font-weight-semi)',
                    color: 'var(--text)',
                  }}
                >
                  Patient Fields (Online Booking)
                </div>
                <div
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--text-muted)',
                    marginTop: 2,
                  }}
                >
                  Choose which patient fields appear on the public booking form
                </div>
              </div>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <div
                  style={{
                    position: 'absolute',
                    left: 12,
                    color: 'var(--text-muted)',
                    display: 'flex',
                    pointerEvents: 'none',
                  }}
                >
                  <NavIcon name="search" />
                </div>
                <input
                  type="text"
                  placeholder="Search fields..."
                  value={fieldSearch}
                  onChange={(e) => setFieldSearch(e.target.value)}
                  style={searchInputStyle}
                />
              </div>
            </div>
          </div>

          {/* Fields table */}
          <div style={{ marginTop: 'var(--sp-4)' }}>
            {/* Header */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '70px 100px 1fr 100px',
                gap: 'var(--sp-3)',
                alignItems: 'center',
                padding: 'var(--sp-3) var(--sp-6)',
                borderBottom: '1px solid var(--border)',
                borderTop: '1px solid var(--border)',
              }}
            >
              <span style={thStyle}>ENABLED</span>
              <span style={thStyle}>REQUIRED</span>
              <span style={thStyle}>FIELD</span>
              <span style={thStyle}>SECTION</span>
            </div>

            {/* Rows */}
            {filteredFields.map((field) => (
              <div
                key={field.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '70px 100px 1fr 100px',
                  gap: 'var(--sp-3)',
                  alignItems: 'center',
                  padding: 'var(--sp-3) var(--sp-6)',
                  borderBottom: '1px solid var(--border)',
                  opacity: field.enabled ? 1 : 0.5,
                }}
              >
                <Toggle
                  checked={field.enabled}
                  onChange={() => handleToggleField(field.id)}
                  disabled={field.locked}
                />
                <div>{field.required && <RequiredBadge />}</div>
                <div>
                  <div
                    style={{
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: 'var(--font-weight-medium)',
                      color: 'var(--text)',
                    }}
                  >
                    {field.label}
                  </div>
                  <div
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--text-muted)',
                    }}
                  >
                    {field.fieldKey}
                  </div>
                </div>
                <Badge
                  label={field.section}
                  bg={field.sectionColor.bg}
                  color={field.sectionColor.color}
                />
              </div>
            ))}
          </div>
        </Card>

        {/* Card 6 — Default Appointment Settings */}
        <Card>
          <div
            style={{
              fontSize: 'var(--font-size-lg)',
              fontWeight: 'var(--font-weight-bold)',
              color: 'var(--text)',
              marginBottom: 'var(--sp-4)',
            }}
          >
            Default Appointment Settings
          </div>
          <div>
            <label
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-medium)',
                color: 'var(--text)',
                display: 'block',
                marginBottom: 'var(--sp-1)',
              }}
            >
              Default appointment status
            </label>
            <select
              value={localSettings.defaultAppointmentStatusId ?? ''}
              onChange={(e) =>
                setLocalSettings((prev) => ({
                  ...prev,
                  defaultAppointmentStatusId: e.target.value || null,
                }))
              }
              style={{
                height: 'var(--input-height)',
                padding: '0 var(--input-padding-x)',
                borderRadius: 'var(--input-radius)',
                border: '1px solid var(--border-input)',
                background: 'var(--input-bg)',
                fontSize: 'var(--font-size-base)',
                color: 'var(--input-text)',
                fontFamily: 'inherit',
                width: 260,
                cursor: 'pointer',
                appearance: 'auto',
              }}
            >
              <option value="">— Select —</option>
              {statusOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <div
              style={{
                fontSize: 'var(--font-size-xs)',
                color: 'var(--text-muted)',
                marginTop: 4,
              }}
            >
              Status automatically applied to new appointments
            </div>
          </div>
        </Card>

        {/* Card 7 — Queue Rules (Coming Soon) */}
        <Card>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
            <span
              style={{
                fontSize: 'var(--font-size-lg)',
                fontWeight: 'var(--font-weight-semi)',
                color: 'var(--text)',
              }}
            >
              Queue Rules
            </span>
            <span
              style={{
                height: 22,
                padding: '0 var(--sp-2)',
                borderRadius: 'var(--radius-xs)',
                background: 'var(--bg-surface-2)',
                color: 'var(--text-muted)',
                fontSize: 'var(--font-size-xs)',
                fontWeight: 'var(--font-weight-medium)',
                lineHeight: '22px',
                fontStyle: 'italic',
              }}
            >
              Coming soon
            </span>
          </div>
          <div
            style={{
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text-muted)',
              marginTop: 'var(--sp-2)',
            }}
          >
            Configure queue rules and check-in workflow for online bookings.
          </div>
        </Card>
      </div>
    </div>
  );
}

// ─── Shared styles ───

const thStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-semi)',
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
};

const searchInputStyle: CSSProperties = {
  width: 200,
  height: 36,
  padding: '0 var(--sp-4) 0 40px',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border)',
  background: 'var(--bg-surface)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--text)',
  outline: 'none',
};

const inputReadOnlyStyle: CSSProperties = {
  height: 'var(--input-height)',
  padding: '0 var(--input-padding-x)',
  borderRadius: 'var(--input-radius)',
  border: '1px solid var(--border-input)',
  background: 'var(--input-bg-readonly)',
  fontSize: 'var(--font-size-base)',
  color: 'var(--input-text-readonly)',
  fontFamily: 'inherit',
  boxSizing: 'border-box',
};

const outlineBtnStyle: CSSProperties = {
  height: 'var(--input-height)',
  padding: '0 var(--sp-4)',
  borderRadius: 'var(--input-radius)',
  border: '1px solid var(--border)',
  background: 'var(--bg-surface)',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-medium)',
  color: 'var(--text)',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--sp-2)',
  whiteSpace: 'nowrap',
};
